import { useCallback, useEffect, useMemo, useState } from 'react'
import { SectionCard } from '../../components/card'
import { Separator } from '../../components/separator'
import { Stepper } from '../../components/stepper'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import { stepperItems } from '../../utils/shared-dropdown'
import ApplicationCooLayout from '../../layout/application-coo-layout'
import DocumentationLists from './documents/documentation-list'

import { usePrepareDocuments } from '../../hooks/use-prepare-documents'
import { SectionLoader } from '../../components/loader'
import ConfettiBanner from '../../components/banner/confetti-banner'
import { FileItem } from '../../models/polymer/Document.model'
import { updateFormData } from '../../redux/slices/application-step-4-slice'
import { TextSubheader } from '../../components/text/text-subheader'
import useSaveDraft from '../../hooks/use-save-draft'
import { ApplicationStep4 } from '../../models/classes/ApplicationStep4.class'
import { P2Button, P2ButtonGroup, P2Modal, P2TextDescription } from 'react-bootstrap-common-script'
import { updateStageAStep4 } from '../../redux/slices/completed-steps-slice'
import useIdleSaveDraft from '../../hooks/use-idle-save-draft'
import { useInitialWindowScrollTo } from '../../hooks/use-initial-window-scroll-to'
import config from '../../config'
import SaveDraftAlert from '../../components/HOC/stage-a-form/save-draft-alert'
import { PolymerAxios } from '../../utils/axios'
import { API_ApplicationRemove } from '../../utils/api'
import BackConfirmationModal from './back-confirmation-modal'
import LeavingPageModal from './leaving-page-modal'
import SaveDraftModal from './save-draft-modal'
import moment from 'moment'
import { setBackTriggered, setLastBackTriggeredTime } from '../../redux/slices/back-triggered-slice'
import { cloneDeep } from 'lodash'
let activeStep = 4;




function ApplicationCOOStep4() {
  const navigate = useNavigate();
  const { performSaveDraft } = useSaveDraft();
  useIdleSaveDraft(() => {saveDraft(false)});
  const [parentFormValid, setParentFormValid] = useState<boolean>(false);
  const [saveDraftSuccess, setSaveDraftSuccess] = useState<boolean>(false);
  const startApplicationFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const applicationStep4FormData = useAppSelector(state => state.app.ApplicationStep4Slice.formData);
  const applicationId = useAppSelector(state => state.app.ApplicationIdSlice.applicationId);
  const backTriggeredSlice = useAppSelector(state => state.app.BackTriggeredSlice);
  const [filesItemsStorage, setFileItemsStorage] = useState<FileItem[]>([...applicationStep4FormData.file_items]);
  const [exceptedDocuments, setExceptedDocuments] = useState<string[]>(cloneDeep(applicationStep4FormData.excepted_documents));
  const [backConfirmationModalOpen, setBackConfirmationModalOpen] = useState<boolean>(false);
  const [saveDraftModalOpen, setSaveDraftModalOpen] = useState<boolean>(false);
  const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
  const [nextModalOpen, setNextModalOpen] = useState<boolean>(false);

  const { isPreparing, documentList, updateLastWillIsChecked } = usePrepareDocuments();
  const step_3 = useAppSelector(state => state.app.CompletedStepsSlice.stage_a.step_3);
  
  const queryParameters = new URLSearchParams(window.location.search);
  const bypass = queryParameters.get("bypass");

  // Derived values
  const successfulFileItems = useMemo(() => filesItemsStorage.filter((file) => file.file_id !== ""), [filesItemsStorage]);

  const AppDispatch = useAppDispatch();

  useEffect(()=>{
    if(!step_3){
      navigate('/application-coo/stage-a/declaration')
    }
  },[step_3, navigate])

  useInitialWindowScrollTo();

  const handleSetFileItemsStorage = ( fileItems: FileItem[]) => {
    setFileItemsStorage(fileItems);
  }

  useEffect(()=>{
    // Everytime fileItemStorage state is updated. Sync successful items with redux
    console.log("useEffect triggered", filesItemsStorage)
    console.log("useEffect successfulFileItems", successfulFileItems)
    AppDispatch(updateFormData(new ApplicationStep4({file_items: successfulFileItems, excepted_documents: exceptedDocuments})));
  },[filesItemsStorage])
  

  const updateStep4Slice = useCallback(() => {
    console.log("Debug updateStep4Slice called. successfulFileItems:", successfulFileItems);
    AppDispatch(updateFormData(new ApplicationStep4({file_items: successfulFileItems, excepted_documents: exceptedDocuments})));
  }, [successfulFileItems, exceptedDocuments])

  const saveDraft = useCallback(async(requiredAlert: boolean) => {
    const response = await performSaveDraft(new ApplicationStep4({file_items: successfulFileItems, excepted_documents: exceptedDocuments}), 4, applicationId, singpassToken)
    if(requiredAlert){
      setSaveDraftSuccess(response);
      updateStep4Slice();
    }
  }, [applicationId, singpassToken, successfulFileItems, exceptedDocuments, startApplicationFormData, updateStep4Slice])

  const navigateBack = useCallback(()=>{
    navigate("/application-coo/stage-a/conveyancing")
  },[navigate])

  const saveValuesAndGoBack = useCallback(() => {
    updateStep4Slice();
    AppDispatch(setBackTriggered(true));
    navigateBack();
  }, [navigateBack, updateStep4Slice])

  const toggleBackConfirmationModal = useCallback(() => {
    const currentTimeString = moment().format('yyyy-MM-DD HH:mm:ss');
    const alreadyTriggered = sessionStorage.getItem("back_triggered");
    if (
      alreadyTriggered !== "true" &&
      backTriggeredSlice.is_back_triggered && 
      backConfirmationModalOpen === false &&
      (backTriggeredSlice.last_back_triggered_time === "" ||
        moment(currentTimeString).diff(moment(backTriggeredSlice.last_back_triggered_time), "seconds") >= 600)
    ) {
      sessionStorage.setItem("back_triggered", "true");
      AppDispatch(setLastBackTriggeredTime(currentTimeString));
      AppDispatch(setBackTriggered(false));
      setBackConfirmationModalOpen((prev) => !prev);
    }else{
      setBackConfirmationModalOpen(false);
    }
  }, [backConfirmationModalOpen, backTriggeredSlice]);

  const toggleSaveDraftModal = useCallback(() => {
    setSaveDraftModalOpen((prev) => !prev);
}, [saveDraftModalOpen])

  const toggleNextStepModal = useCallback(() => {
      setNextModalOpen((prev) => !prev);
  }, [nextModalOpen])

  const handleSaveDraft = useCallback(() => {
    saveDraft(true);
    updateStep4Slice();
    setSaveDraftModalOpen(false);
  }, [saveDraft, updateStep4Slice])

  const saveDraftLeavePage = useCallback(async()=>{
    await saveDraft(false);
  },[navigate, saveDraft])

  const removeApplication = useCallback(async () => {
    if(applicationId){
      try {
        await PolymerAxios(loginUser?.uinfin ?? "", singpassToken)
          .post(`${API_ApplicationRemove}/${applicationId}`)
          .then((res) => res.data);
      } catch (error) {
        console.log(error);
      }
    }
  }, [singpassToken, applicationId, loginUser]);



  const proceedNext = useCallback(() => {
    updateStep4Slice();
    performSaveDraft(new ApplicationStep4({file_items: successfulFileItems, excepted_documents: exceptedDocuments}),4, applicationId, singpassToken)
    AppDispatch(updateStageAStep4(true));
    navigate('/application-coo/stage-a/summary');
  }, [applicationId, performSaveDraft, navigate, singpassToken, successfulFileItems, exceptedDocuments, updateStep4Slice])

  useEffect(() => {
    if(saveDraftSuccess){
        setTimeout(() => {
            setSaveDraftSuccess(false);
        }, 3000)
    }
}, [saveDraftSuccess])


  const isBtnValid = useMemo((): boolean => {
    //Localhost to allow bypass.
    if(config.isLocalhost || bypass){
      return true;
    }
    return parentFormValid || documentList.length === 0;
  }, [ parentFormValid, documentList, bypass ]);

  const getAdditionalCheckbox = (additionalCheckbox: string[]) => {
    setExceptedDocuments(additionalCheckbox.filter(item => documentList.some(doc => doc.label === item)));
    updateLastWillIsChecked(additionalCheckbox);
  }


  return (
    <ApplicationCooLayout>
      <div className='polymer-application-coo__wrapper'>
        <div>
          <TextSubheader subheaderMain="Stage A: Start Application" subheaderSub={startApplicationFormData.selected_hdb_property?.address} paddingLeftRight={42}/>
        </div>
        <Stepper activeStep={activeStep} items={stepperItems} style={{ margin: "62px auto" }} />
        <div className='d-flex justify-content-center'>
          <SectionCard onClick={toggleBackConfirmationModal} className='flex-fill' activeStep={activeStep} stepperItems={stepperItems} titleChildren={ <P2TextDescription title={stepperItems[activeStep - 1]?.title} />}
            contentChildren={
              <div>
                {!isPreparing && <div className='px-4'><DocumentationLists type={1} document_list={documentList} setParentFormValid={setParentFormValid} setFileItemsStorage={handleSetFileItemsStorage} filesItemsStorage={filesItemsStorage} getAdditionalCheckbox={getAdditionalCheckbox} defaultCheckboxList={cloneDeep(applicationStep4FormData.excepted_documents)}  hdbRef={startApplicationFormData.selected_hdb_property?.hdb_ref}
                  uinfin={loginUser?.uinfin}
                  singpassToken={singpassToken}
                  applicationId={applicationId}
                  /></div>}
                {!isPreparing && documentList.length === 0 && <ConfettiBanner style={{margin: "40px 0"}}/>}
                {isPreparing && <SectionLoader style={{ padding: "64px 24px" }} />}
              </div>
            }
          />
        </div>

        <Separator style={{ margin: "32px 42px" }} />

        <div className='polymer-shared-alert-container'>
            <div style={{ marginTop: 24, marginBottom: 24 }}>
            {saveDraftSuccess && <SaveDraftAlert/>}
            </div>
        </div>
        <BackConfirmationModal onSubmit={toggleBackConfirmationModal} onClose={toggleBackConfirmationModal} isOpen={backConfirmationModalOpen}/>

        <LeavingPageModal 
          onSubmit={saveDraftLeavePage}
          onCancel={removeApplication}
          filesItemsStorage={filesItemsStorage}
        /> 
        
        
        <SaveDraftModal onSubmit={handleSaveDraft} onCancel={removeApplication} onClose={toggleSaveDraftModal} isOpen={saveDraftModalOpen}/>

        <div className='polymer-application-coo__card-button'>
          <P2ButtonGroup>
            <P2Button onClick={saveValuesAndGoBack} variant="outline-primary">Back</P2Button>
            <P2Button variant={"outline-primary"} onClick={toggleSaveDraftModal}>Save as draft</P2Button>
            <P2Button onClick={toggleNextStepModal} disabled={!isBtnValid} variant="primary-1">Next</P2Button>
          </P2ButtonGroup>
        </div>
      </div>
      <P2Modal
        className="polymer-application-coo__a4_next_modal"
        isOpen={nextModalOpen}
        onCloseModal={toggleNextStepModal}
        variant="default"
        title={`Confirm to proceed?`}
        primaryButtonText="Cancel"
        secondaryButtonText="Confirm"
        primaryButtonFunction={toggleNextStepModal}
        secondaryButtonFunction={proceedNext}
        children={
          <p>Kindly ensure that all documents are in order and that the application is accurate. If not, HDB will have to reject the application and any subsequent payments made to submit the application will not be refunded. </p>
        }
      />
    </ApplicationCooLayout>


  )
}

export default ApplicationCOOStep4
